import type { AlgorithmSpec } from "./types";

export const KIND_OPTIONS = [
  { value: "ssh", label: "SSH key" },
  { value: "pgp", label: "PGP key" },
  { value: "jwk", label: "JSON Web Key" },
  { value: "wireguard", label: "WireGuard keys" },
  { value: "age", label: "age identity" },
  { value: "nacl", label: "NaCl box keys" },
  { value: "secret", label: "Random secret" },
];

export const KIND_LABELS: Record<string, string> = {
  ssh: "SSH key pair",
  pgp: "PGP key pair",
  jwk: "JSON Web Key",
  wireguard: "WireGuard configuration",
  age: "age identity",
  nacl: "NaCl box key pair",
  secret: "Random secret",
};

export const RSA_SIZES = [
  { value: "3072", label: "3072 bits" },
  { value: "2048", label: "2048 bits" },
  { value: "4096", label: "4096 bits" },
];

// The two things a JWK can be for, which for these algorithms is the whole of what `use` can say.
export const SIGNATURE = "Signature";
export const ENCRYPTION = "Encryption";

// The RSA families all leave the same choice, and so do the two agreement ones, so each says it once.
export const RSA_SIZE = { variantLabel: "Key size", variants: RSA_SIZES };

// The `crv` names as a JWK spells them, which is also what Web Crypto is asked for.
export const ECDH_CURVE = {
  variantLabel: "Curve",
  variants: [
    { value: "P-256", label: "NIST P-256" },
    { value: "P-384", label: "NIST P-384" },
    { value: "P-521", label: "NIST P-521" },
    { value: "X25519", label: "X25519" },
  ],
};

// The SEC names OpenSSH prints, which is also how `WEB_CRYPTO_CURVES` below is keyed.
export const NIST_CURVES = [
  { value: "nistp256", label: "NIST P-256" },
  { value: "nistp384", label: "NIST P-384" },
  { value: "nistp521", label: "NIST P-521" },
];

export const ALGORITHMS: Record<string, AlgorithmSpec[]> = {
  ssh: [
    { value: "ed25519", label: "Ed25519" },
    { value: "ecdsa", label: "ECDSA", variantLabel: "Curve", variants: NIST_CURVES },
    { value: "rsa", label: "RSA", variantLabel: "Key size", variants: RSA_SIZES },
  ],
  pgp: [
    { value: "curve25519", label: "Curve25519" },
    {
      value: "ecc",
      label: "ECDSA",
      variantLabel: "Curve",
      variants: [
        { value: "nistP256", label: "NIST P-256" },
        { value: "nistP384", label: "NIST P-384" },
        { value: "nistP521", label: "NIST P-521" },
      ],
    },
    { value: "rsa", label: "RSA", variantLabel: "Key size", variants: RSA_SIZES },
  ],
  // The JWA names of RFC 7518, since the algorithm is a member of the key rather than something around it, and
  // it is also what says whether the key signs or encrypts — which is the group each one is under.
  jwk: [
    { group: SIGNATURE, value: "EdDSA", label: "EdDSA (Ed25519)" },
    { group: SIGNATURE, value: "ES256", label: "ES256 (P-256)" },
    { group: SIGNATURE, value: "ES384", label: "ES384 (P-384)" },
    { group: SIGNATURE, value: "ES512", label: "ES512 (P-521)" },
    { group: SIGNATURE, value: "RS256", label: "RS256 (PKCS#1 v1.5, SHA-256)", ...RSA_SIZE },
    { group: SIGNATURE, value: "RS384", label: "RS384 (PKCS#1 v1.5, SHA-384)", ...RSA_SIZE },
    { group: SIGNATURE, value: "RS512", label: "RS512 (PKCS#1 v1.5, SHA-512)", ...RSA_SIZE },
    { group: SIGNATURE, value: "PS256", label: "PS256 (PSS, SHA-256)", ...RSA_SIZE },
    { group: SIGNATURE, value: "PS384", label: "PS384 (PSS, SHA-384)", ...RSA_SIZE },
    { group: SIGNATURE, value: "PS512", label: "PS512 (PSS, SHA-512)", ...RSA_SIZE },
    { group: SIGNATURE, value: "HS256", label: "HS256 (SHA-256)" },
    { group: SIGNATURE, value: "HS384", label: "HS384 (SHA-384)" },
    { group: SIGNATURE, value: "HS512", label: "HS512 (SHA-512)" },
    { group: ENCRYPTION, value: "ECDH-ES", label: "ECDH-ES (direct agreement)", ...ECDH_CURVE },
    { group: ENCRYPTION, value: "ECDH-ES+A128KW", label: "ECDH-ES+A128KW", ...ECDH_CURVE },
    { group: ENCRYPTION, value: "ECDH-ES+A192KW", label: "ECDH-ES+A192KW", ...ECDH_CURVE },
    { group: ENCRYPTION, value: "ECDH-ES+A256KW", label: "ECDH-ES+A256KW", ...ECDH_CURVE },
    { group: ENCRYPTION, value: "RSA-OAEP-256", label: "RSA-OAEP-256 (SHA-256)", ...RSA_SIZE },
    { group: ENCRYPTION, value: "RSA-OAEP-384", label: "RSA-OAEP-384 (SHA-384)", ...RSA_SIZE },
    { group: ENCRYPTION, value: "RSA-OAEP-512", label: "RSA-OAEP-512 (SHA-512)", ...RSA_SIZE },
    { group: ENCRYPTION, value: "RSA-OAEP", label: "RSA-OAEP (SHA-1)", ...RSA_SIZE },
    { group: ENCRYPTION, value: "A128KW", label: "A128KW (AES key wrap)" },
    { group: ENCRYPTION, value: "A192KW", label: "A192KW (AES key wrap)" },
    { group: ENCRYPTION, value: "A256KW", label: "A256KW (AES key wrap)" },
    { group: ENCRYPTION, value: "A128GCMKW", label: "A128GCMKW (AES-GCM key wrap)" },
    { group: ENCRYPTION, value: "A192GCMKW", label: "A192GCMKW (AES-GCM key wrap)" },
    { group: ENCRYPTION, value: "A256GCMKW", label: "A256GCMKW (AES-GCM key wrap)" },
  ],
  // WireGuard is X25519 and nothing else, as is NaCl's crypto_box, and a random secret is not a key of any algorithm
  // at all — the encoding those last two are written in is the whole of what either leaves to be picked. age has no
  // Algorithm select either: `age-keygen` takes a flag rather than an algorithm, so the page draws the flag.
  wireguard: [],
  nacl: [],
  age: [],
  secret: [],
};

// The two invocations `age-keygen` has: one with no input, and `-y`, which reads an identity file rather than
// writing one. It is a second output rather than a second key kind, the identity going in being one of these.
export const AGE_OUTPUT_OPTIONS = [
  { value: "identity", label: "New identity" },
  { value: "recipients", label: "Recipients file" },
];

export const KEY_ID_OPTIONS = [
  { value: "none", label: "None" },
  { value: "uuid", label: "Random UUID" },
  { value: "timestamp", label: "Timestamp" },
  { value: "iso", label: "ISO date" },
  { value: "sha256", label: "SHA-256 thumbprint" },
  { value: "sha1", label: "SHA-1 thumbprint" },
];

export const FORMAT_OPTIONS = [
  { value: "hex", label: "Hexadecimal" },
  { value: "hex-upper", label: "Hexadecimal (uppercase)" },
  { value: "base64", label: "Base64" },
  { value: "base64url", label: "Base64 (URL-safe)" },
  { value: "base32", label: "Base32" },
  { value: "decimal", label: "Decimal" },
];

// Long enough for any key material anyone hands to a cipher, and short enough that the box still reads as one value.
export const MAX_SECRET_BYTES = 512;
export const DEFAULT_SECRET_BYTES = 32;

// Enough keys to stand a rotation up with room to spare, and few enough that the set still reads as one document.
export const MAX_JWK_KEYS = 8;

// ES512 signs with SHA-512 over P-521, which is the one pairing whose two numbers do not match.
export const JWK_CURVES: Record<string, string> = { ES256: "P-256", ES384: "P-384", ES512: "P-521" };

// Every algorithm whose key is a lone secret, and how many bytes of one: the hash HMAC signs with, and the
// width of the cipher AES wraps with. Being in here is also what says a key has no public half to hand out.
export const JWK_SECRET_BYTES: Record<string, number> = {
  HS256: 32,
  HS384: 48,
  HS512: 64,
  A128KW: 16,
  A192KW: 24,
  A256KW: 32,
  A128GCMKW: 16,
  A192GCMKW: 24,
  A256GCMKW: 32,
};

// The key material RFC 7517 and RFC 8037 name, in the order each family is usually written out in.
export const JWK_MEMBERS = ["crv", "n", "e", "x", "y", "d", "p", "q", "dp", "dq", "qi", "k"];

// RFC 7638 hashes the required members alone, which is what makes the thumbprint the same for either half.
export const THUMBPRINT_MEMBERS: Record<string, string[]> = {
  EC: ["crv", "kty", "x", "y"],
  OKP: ["crv", "kty", "x"],
  RSA: ["e", "kty", "n"],
  oct: ["k", "kty"],
};

// The curve names OpenSSH prints are the SEC names; Web Crypto asks for the NIST ones.
export const WEB_CRYPTO_CURVES: Record<string, string> = {
  nistp256: "P-256",
  nistp384: "P-384",
  nistp521: "P-521",
};

export function algorithmSpec(kind: string, algorithm: string): AlgorithmSpec | undefined {
  return ALGORITHMS[kind]?.find((spec) => spec.value === algorithm);
}

// Mantine reads a heading off the shape around a run of items — and reads a `group` key on an item as one too,
// so it is handed the value and label alone, whatever else the entry carries for the page's own use.
export function algorithmData(specs: AlgorithmSpec[]) {
  const items = specs.map(({ value, label }) => ({ value, label }));
  if (!specs.some((spec) => spec.group)) return items;

  const groups: { group: string; items: typeof items }[] = [];
  specs.forEach((spec, index) => {
    const last = groups[groups.length - 1];
    if (last?.group === spec.group) last.items.push(items[index]);
    else groups.push({ group: spec.group ?? "", items: [items[index]] });
  });
  return groups;
}

export function pickKind(value: unknown): string {
  return typeof value === "string" && value in ALGORITHMS ? value : "ssh";
}

export function pickAlgorithm(kind: string, value: unknown): string {
  const specs = ALGORITHMS[kind] ?? [];
  if (specs.length === 0) return "";
  return specs.some((spec) => spec.value === value) ? value as string : specs[0].value;
}

export function pickVariant(kind: string, algorithm: string, value: unknown): string {
  const variants = algorithmSpec(kind, algorithm)?.variants;
  if (!variants) return "";
  return variants.some((item) => item.value === value) ? value as string : variants[0].value;
}

export function pickKeyIdSource(value: unknown): string {
  return KEY_ID_OPTIONS.some((option) => option.value === value) ? value as string : "sha256";
}

export function pickAgeOutput(value: unknown): string {
  return AGE_OUTPUT_OPTIONS.some((option) => option.value === value) ? value as string : "identity";
}

// `age-keygen -pq` is a flag rather than an algorithm, and it is on unless a link says otherwise: a key minted today
// outlives the curve it was drawn on, and the two kilobytes it costs are paid once by whoever hands the recipient out.
export function pickPostQuantum(value: unknown): boolean {
  return typeof value === "boolean" ? value : true;
}

export function pickFormat(value: unknown): string {
  return FORMAT_OPTIONS.some((option) => option.value === value) ? value as string : "hex";
}

export function pickText(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}
